import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Text, Button, IconButton, Input, Image, SimpleGrid, Skeleton, useToast, FormControl, FormLabel, FormHelperText,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton
} from '@chakra-ui/react';
import { Add, DeleteOutline, CloudUpload } from '@mui/icons-material';

import AdminLayout from '../components/admin/AdminLayout';
import usePublicSettings from '../hooks/usePublicSettings';
import { getAllImages, addImage, deleteImage, getAllMiniImages, addMiniImage, deleteMiniImage, uploadImageToCloudinary } from '../services/ImageServices';


const SECTIONS = {
  slides: { title: 'Homepage slides', hint: 'Wide banner, 1200 × 660 px. Rotates at the top of the home page.', ratio: '55%', load: () => getAllImages().then(r => r.images || []), add: addImage, remove: deleteImage },
  tiles: { title: 'Promo tiles', hint: '600 × 340 px. Shown below New arrivals.', ratio: '56.7%', load: () => getAllMiniImages().then(r => r.miniImages || []), add: addMiniImage, remove: deleteMiniImage }
};

const ImagesforAdmin = () => {

  const toast = useToast();
  const [images, setImages] = useState({ slides: null, tiles: null });
  const [adding, setAdding] = useState(null);     // section key
  const [removing, setRemoving] = useState(null); // { section, image }

  const load = (key) => SECTIONS[key].load().then(list => setImages(prev => ({ ...prev, [key]: list }))).catch(() => setImages(prev => ({ ...prev, [key]: [] })));
  useEffect(() => { load('slides'); load('tiles'); }, []);

  const onAdd = async (url) => {
    try {
      await SECTIONS[adding].add(url);
      toast({ title: 'Image added', status: 'success', duration: 1500, position: 'top' });
      load(adding);
      setAdding(null);
    } catch (e) {
      toast({ title: 'Could not add image', status: 'error', duration: 2500 });
    }
  };

  const onRemove = async () => {
    const { section, image } = removing;
    try {
      await SECTIONS[section].remove(image._id);
      setImages(prev => ({ ...prev, [section]: prev[section].filter(i => i._id !== image._id) }));
      toast({ title: 'Image removed', status: 'success', duration: 1500, position: 'top' });
    } catch (e) {
      toast({ title: 'Could not remove image', status: 'error', duration: 2500 });
    }
    setRemoving(null);
  };

  return (
    <AdminLayout title='Banners' subtitle='Images on the home page' >
      {Object.entries(SECTIONS).map(([key, section]) => (
        <Box key={key} bg='raano.white' borderRadius='12px' border='1px solid' borderColor='raano.border' p={4} mb={3} >
          <Box display='flex' justifyContent='space-between' alignItems='flex-start' gap={3} mb={3} >
            <Box>
              <Text fontWeight={700} >{section.title} {images[key] && <Text as='span' color='blackAlpha.500' fontWeight={500} >· {images[key].length}</Text>}</Text>
              <Text fontSize={12} color='blackAlpha.600' >{section.hint}</Text>
            </Box>
            <Button variant='wipe' height='36px' px='14px' fontSize='13px' leftIcon={<Add fontSize='small' />} onClick={() => setAdding(key)} flexShrink={0} >Add</Button>
          </Box>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={3} >
            {images[key] === null
              ? <Skeleton paddingTop={section.ratio} borderRadius='8px' />
              : images[key].length === 0
                ? <Text fontSize={14} color='blackAlpha.600' >No images yet.</Text>
                : images[key].map((image, i) => (
                  <Box key={image._id} position='relative' borderRadius='8px' overflow='hidden' border='1px solid' borderColor='raano.border' >
                    <Box paddingTop={section.ratio} bgImage={`url(${image.url})`} bgSize='cover' bgPosition='center' bgColor='raano.imageBg' />
                    <Text position='absolute' top={2} left={2} fontSize={11} fontWeight={700} bg='blackAlpha.700' color='#fff' px={2} py={0.5} borderRadius='full' >{i + 1}</Text>
                    <IconButton position='absolute' top={2} right={2} size='sm' borderRadius='full' bg='whiteAlpha.900' _hover={{ bg: '#fff', color: 'red.600' }} aria-label='Remove image' icon={<DeleteOutline fontSize='small' />} onClick={() => setRemoving({ section: key, image })} />
                  </Box>
                ))}
          </SimpleGrid>
        </Box>
      ))}

      <AddImageDialog isOpen={!!adding} section={adding && SECTIONS[adding]} onClose={() => setAdding(null)} onAdd={onAdd} />

      <Modal isOpen={!!removing} onClose={() => setRemoving(null)} isCentered >
        <ModalOverlay />
        <ModalContent mx={4} borderRadius='12px' >
          <ModalHeader fontSize={18} >Remove this image?</ModalHeader>
          <ModalBody>
            {removing && <Image src={removing.image.url} alt='' borderRadius='8px' />}
            <Text fontSize={14} color='blackAlpha.700' mt={3} >It will disappear from the home page straight away.</Text>
          </ModalBody>
          <ModalFooter gap={2} >
            <Button borderRadius='full' onClick={() => setRemoving(null)} >Keep</Button>
            <Button borderRadius='full' colorScheme='red' onClick={onRemove} >Remove</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}

const AddImageDialog = ({ isOpen, section, onClose, onAdd }) => {
  const toast = useToast();
  const CAN_UPLOAD = Boolean(usePublicSettings()?.cloudinary);
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => { if (isOpen) setUrl(''); }, [isOpen]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadImageToCloudinary(file);
      if (!result.secure_url) throw new Error(result.error?.message || 'Upload failed');
      setUrl(result.secure_url);
    } catch (error) {
      toast({ title: 'Upload failed', description: error.message, status: 'error', duration: 3000 });
    }
    setUploading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered >
      <ModalOverlay />
      <ModalContent mx={4} borderRadius='12px' >
        <ModalHeader fontSize={18} >Add to {section?.title.toLowerCase()}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {url && <Box paddingTop={section?.ratio} bgImage={`url(${url})`} bgSize='cover' bgPosition='center' borderRadius='8px' mb={3} bgColor='raano.imageBg' />}
          {CAN_UPLOAD &&
            <Button width='100%' mb={3} borderRadius='full' leftIcon={<CloudUpload fontSize='small' />} isLoading={uploading} onClick={() => fileRef.current?.click()} >Upload image</Button>}
          <input ref={fileRef} type='file' accept='image/*' hidden onChange={onUpload} />
          <FormControl>
            <FormLabel fontSize={14} mb={1} >{CAN_UPLOAD ? 'Or paste an image link' : 'Image link'}</FormLabel>
            <Input placeholder='https://…' value={url} onChange={(e) => setUrl(e.target.value)} />
            <FormHelperText fontSize={12} >{section?.hint}</FormHelperText>
          </FormControl>
        </ModalBody>
        <ModalFooter gap={2} >
          <Button variant='wipe' height='40px' px='18px' fontSize='14px' onClick={onClose} >Cancel</Button>
          <Button variant='ink' height='40px' px='18px' fontSize='14px' isDisabled={!/^(https?:\/\/|\/)/.test(url.trim())} onClick={() => onAdd(url.trim())} >Add image</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ImagesforAdmin;
